import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabase';
import { useAuth } from '../context/AuthContext';
import { useFieldIntel } from './FieldIntelContext';
import useSpeechToText from './useSpeechToText';

/**
 * The fast path: log a call without leaving the feed.
 *
 * Built for a conference booth - a physician walks up, the rep opens this,
 * types two letters of the name, taps them, talks, saves. Everything that is a
 * dropdown on the full call-log screen is deliberately absent here; anyone who
 * needs those taps "View Full Card" instead.
 *
 * Follow-up is one tap rather than a date picker, since that handoff is the
 * thing that gets dropped - but it stays optional.
 */

const MAX_RESULTS = 25;

const QuickLogCallModal = ({ onClose }) => {
  const { user } = useAuth();
  const { role } = useFieldIntel();
  const navigate = useNavigate();

  const [surgeons, setSurgeons] = useState([]);
  const [loadingSurgeons, setLoadingSurgeons] = useState(true);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [followUpDate, setFollowUpDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // The hook owns the summary text so the box fills live while they talk.
  const dictation = useSpeechToText('');
  const summary = dictation.text;

  // Same scoping as the full call-log screen: reps see their book, everyone
  // above sees what RLS allows.
  useEffect(() => {
    if (!user?.id || !role) return;
    let cancelled = false;

    const load = async () => {
      setLoadingSurgeons(true);
      try {
        let data = [];
        if (role === 'rep') {
          const { data: dels } = await supabase
            .from('account_delegations')
            .select('surgeon_id')
            .eq('user_id', user.id);
          const ids = (dels || []).map(d => d.surgeon_id);
          if (ids.length > 0) {
            const { data: s } = await supabase
              .from('surgeons')
              .select('id, full_name, first_name, last_name, specialty, city, state')
              .in('id', ids)
              .order('full_name');
            data = s || [];
          }
        } else {
          const { data: s } = await supabase
            .from('surgeons')
            .select('id, full_name, first_name, last_name, specialty, city, state')
            .order('full_name');
          data = s || [];
        }
        if (!cancelled) setSurgeons(data);
      } catch (err) {
        console.error('[QuickLogCall] Error fetching surgeons:', err);
      } finally {
        if (!cancelled) setLoadingSurgeons(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [user?.id, role]);

  const nameOf = (s) => s.full_name || `${s.first_name || ''} ${s.last_name || ''}`.trim();

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return surgeons.slice(0, MAX_RESULTS);
    return surgeons
      .filter((s) => {
        const haystack = [nameOf(s), s.city, s.state, s.specialty]
          .filter(Boolean).join(' ').toLowerCase();
        return haystack.includes(q);
      })
      .slice(0, MAX_RESULTS);
  }, [surgeons, query]);

  const goTo = (path) => {
    if (dictation.listening) dictation.stop();
    onClose();
    navigate(path);
  };

  const canSave = Boolean(selected && summary.trim() && !saving);

  const handleSave = async () => {
    if (!canSave) return;
    if (dictation.listening) await dictation.stop();
    setSaving(true);
    setError(null);

    try {
      const { data: inserted, error: insertErr } = await supabase
        .from('call_logs')
        .insert({
          surgeon_id: selected.id,
          user_id: user.id,
          call_date: new Date().toISOString().split('T')[0],
          summary: summary.trim(),
          follow_up_date: followUpDate || null,
        })
        .select('id')
        .single();
      if (insertErr) throw insertErr;

      // The whole point of the booth flow: the rep and manager who own this
      // account hear about it. Never let a push failure lose the saved log.
      if (inserted?.id) {
        supabase.functions
          .invoke('send-call-log-push', {
            body: {
              call_log_id: inserted.id,
              surgeon_id: selected.id,
              author_user_id: user.id,
            },
          })
          .catch(err => console.error('[QuickLogCall] push notify failed:', err));
      }

      onClose();
    } catch (err) {
      console.error('[QuickLogCall] Save error:', err);
      setError('Could not save the call log. Please try again.');
      setSaving(false);
    }
  };

  const quickDates = [
    { label: '1 mo', months: 1 },
    { label: '3 mo', months: 3 },
    { label: '6 mo', months: 6 },
  ].map(({ label, months }) => {
    const d = new Date();
    d.setMonth(d.getMonth() + months);
    return { label, value: d.toISOString().split('T')[0] };
  });

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.card} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <span style={styles.title}>{selected ? nameOf(selected) : 'Log a Call'}</span>
          <button onClick={onClose} style={styles.closeBtn} aria-label="Close">
            <CloseIcon />
          </button>
        </div>

        {!selected ? (
          <>
            <div style={styles.searchWrap}>
              <SearchIcon />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Start typing a doctor's name..."
                style={styles.searchInput}
              />
            </div>

            <div style={styles.list}>
              {loadingSurgeons && <div style={styles.muted}>Loading accounts...</div>}
              {!loadingSurgeons && results.length === 0 && (
                <div style={styles.muted}>No accounts match that search.</div>
              )}
              {results.map(s => (
                <button key={s.id} onClick={() => setSelected(s)} style={styles.resultRow}>
                  <span style={styles.resultName}>{nameOf(s)}</span>
                  <span style={styles.resultMeta}>
                    {[s.specialty, [s.city, s.state].filter(Boolean).join(', ')]
                      .filter(Boolean).join(' · ')}
                  </span>
                </button>
              ))}
            </div>

            <button onClick={() => goTo('/field-intel/leads/new')} style={styles.addAccountBtn}>
              <PlusIcon /> Add an account
              <span style={styles.addAccountHint}>Sends to the admin team for review</span>
            </button>
          </>
        ) : (
          <>
            <button onClick={() => { setSelected(null); setQuery(''); }} style={styles.changeBtn}>
              Choose a different doctor
            </button>

            <div style={styles.fieldGroup}>
              <div style={styles.labelRow}>
                <span style={styles.label}>Summary</span>
                {dictation.supported && (
                  <button
                    onClick={dictation.toggle}
                    style={dictation.listening ? styles.micBtnActive : styles.micBtn}
                  >
                    <MicIcon />
                    {dictation.listening ? 'Stop' : 'Speak'}
                  </button>
                )}
              </div>
              <textarea
                value={summary}
                onChange={(e) => dictation.setText(e.target.value)}
                placeholder={dictation.listening ? 'Listening...' : 'What happened on this call?'}
                rows={4}
                style={styles.textarea}
              />
              {dictation.error && <span style={styles.warn}>{dictation.error}</span>}
            </div>

            <div style={styles.fieldGroup}>
              <span style={styles.label}>Follow up in (optional)</span>
              <div style={styles.chipRow}>
                {quickDates.map(({ label, value }) => (
                  <button
                    key={label}
                    onClick={() => setFollowUpDate(value)}
                    style={followUpDate === value ? styles.chipActive : styles.chip}
                  >
                    {label}
                  </button>
                ))}
                <input
                  type="date"
                  value={followUpDate}
                  onChange={(e) => setFollowUpDate(e.target.value)}
                  style={styles.dateInput}
                />
              </div>
            </div>

            {error && <span style={styles.error}>{error}</span>}

            <button
              onClick={handleSave}
              disabled={!canSave}
              style={canSave ? styles.saveBtn : styles.saveBtnDisabled}
            >
              {saving ? 'Saving...' : 'Log Call'}
            </button>
            <button
              onClick={() => goTo(`/field-intel/dossier/${selected.id}`)}
              style={styles.fullCardBtn}
            >
              View Full Card
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const PlusIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const MicIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
    <line x1="12" y1="19" x2="12" y2="23" />
    <line x1="8" y1="23" x2="16" y2="23" />
  </svg>
);

const chipBase = {
  padding: '6px 12px',
  borderRadius: '999px',
  fontSize: '13px',
  fontWeight: '600',
  cursor: 'pointer',
};

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
    zIndex: 3000,
  },
  card: {
    width: '100%',
    maxWidth: '460px',
    maxHeight: '85vh',
    overflowY: 'auto',
    backgroundColor: '#ffffff',
    borderRadius: '14px',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
  },
  title: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#1e293b',
  },
  closeBtn: {
    padding: '6px',
    backgroundColor: 'transparent',
    color: '#64748b',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    flexShrink: 0,
  },
  searchWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 12px',
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
  },
  searchInput: {
    flex: 1,
    border: 'none',
    outline: 'none',
    backgroundColor: 'transparent',
    fontSize: '14px',
    color: '#1e293b',
    fontFamily: 'inherit',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    maxHeight: '300px',
    overflowY: 'auto',
  },
  resultRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    padding: '10px 12px',
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    cursor: 'pointer',
    textAlign: 'left',
    width: '100%',
  },
  resultName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1e293b',
  },
  resultMeta: {
    fontSize: '12px',
    color: '#64748b',
  },
  addAccountBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flexWrap: 'wrap',
    padding: '11px 12px',
    backgroundColor: '#ffffff',
    color: '#2563eb',
    border: '1px dashed #bfdbfe',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    width: '100%',
  },
  addAccountHint: {
    fontSize: '12px',
    fontWeight: '500',
    color: '#94a3b8',
  },
  changeBtn: {
    alignSelf: 'flex-start',
    padding: 0,
    backgroundColor: 'transparent',
    color: '#2563eb',
    border: 'none',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  labelRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
  },
  label: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  micBtn: {
    ...chipBase,
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    padding: '4px 10px',
    fontSize: '12px',
    backgroundColor: '#ffffff',
    color: '#2563eb',
    border: '1px solid #bfdbfe',
  },
  micBtnActive: {
    ...chipBase,
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    padding: '4px 10px',
    fontSize: '12px',
    backgroundColor: '#dc2626',
    color: '#ffffff',
    border: '1px solid #dc2626',
  },
  textarea: {
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    fontSize: '14px',
    color: '#1e293b',
    outline: 'none',
    backgroundColor: '#ffffff',
    resize: 'vertical',
    fontFamily: 'inherit',
  },
  chipRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
  chip: {
    ...chipBase,
    backgroundColor: '#f8fafc',
    color: '#475569',
    border: '1px solid #e2e8f0',
  },
  chipActive: {
    ...chipBase,
    backgroundColor: '#2563eb',
    color: '#ffffff',
    border: '1px solid #2563eb',
  },
  dateInput: {
    padding: '5px 10px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    fontSize: '13px',
    color: '#1e293b',
    outline: 'none',
    backgroundColor: '#ffffff',
    fontFamily: 'inherit',
  },
  saveBtn: {
    padding: '12px',
    backgroundColor: '#2563eb',
    color: '#ffffff',
    border: '1px solid #2563eb',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    width: '100%',
  },
  saveBtnDisabled: {
    padding: '12px',
    backgroundColor: '#94a3b8',
    color: '#ffffff',
    border: '1px solid #94a3b8',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'default',
    width: '100%',
  },
  fullCardBtn: {
    padding: '11px',
    backgroundColor: '#ffffff',
    color: '#2563eb',
    border: '1px solid #bfdbfe',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    width: '100%',
  },
  muted: {
    fontSize: '13px',
    color: '#94a3b8',
    padding: '12px 4px',
  },
  warn: {
    fontSize: '12px',
    color: '#d97706',
  },
  error: {
    fontSize: '13px',
    color: '#dc2626',
  },
};

export default QuickLogCallModal;
